export const NOTIFICATION_BADGES = [
  { key: "printing", label: "Print Items" },
  { key: "picking", label: "Picking" },
  { key: "checking", label: "Checking" },
  { key: "outgoing", label: "Outgoing" },
  { key: "notices", label: "Notices" },
];

export const isBadgeAllowedForRole = (badgeKey, role) => {
  if (!role) return true;

  const roleName = typeof role === "string" ? role : (role.role || role.name || "");
  if (roleName && (roleName.toLowerCase() === "admin" || roleName.toLowerCase() === "administrator")) {
    return true;
  }

  const hasFullOrders = Boolean(role.orders);

  switch (badgeKey) {
    case "printing":
      return Boolean(role.printing || hasFullOrders);
    case "picking":
      return Boolean(role.ordersPicking || hasFullOrders);
    case "checking":
      return Boolean(role.ordersComplete || hasFullOrders);
    case "outgoing":
      return Boolean(role.ordersDispatch || hasFullOrders);
    case "notices":
      return role.notices !== undefined ? Boolean(role.notices) : true;
    default:
      return true;
  }
};

export const getActiveNotificationBadges = (badges = {}, role = null) =>
  NOTIFICATION_BADGES
    .filter(({ key }) => isBadgeAllowedForRole(key, role))
    .map(({ key, label }) => {
      const rawCount = Number(badges?.[key]) || 0;
      // If items are not printed, count as 1 notification (Print Items - N)
      const count = key === "printing" ? (rawCount > 0 ? 1 : 0) : rawCount;
      return { key, label, count, rawCount };
    })
    .filter(({ count }) => count > 0);

export const getNotificationTotal = (badges = {}, role = null) =>
  getActiveNotificationBadges(badges, role)
    .reduce((total, { count }) => total + count, 0);

export const NOTICE_PAGE_SIZE = 100;
const PANEL_STAGES = ["picking", "checking", "outgoing"];

export const orderIdentity = (order) => `order:${order.orderId}:${order.stage?.toLowerCase()}`;
export const printIdentity = (printCount) => `print:${printCount}`;

// Single source for the bell total and the panel: both render from this, so they cannot disagree.
export const getNotificationSummary = ({ badges = {}, orders = [], notices = [], dismissals = [], role = null } = {}) => {
  const dismissed = new Set(dismissals);
  const visibleOrders = orders.filter((order) => {
    const stage = order.stage?.toLowerCase();
    return PANEL_STAGES.includes(stage) && isBadgeAllowedForRole(stage, role) && !dismissed.has(orderIdentity(order));
  });

  const printCount = isBadgeAllowedForRole("printing", role) ? Number(badges?.printing) || 0 : 0;
  const printKey = printIdentity(printCount);
  const showPrint = printCount > 0 && !dismissed.has(printKey);

  const canSeeNotices = isBadgeAllowedForRole("notices", role);
  const unreadNotices = canSeeNotices ? notices.filter((n) => !n.isRead) : [];
  // The panel loads up to NOTICE_PAGE_SIZE unread notices; beyond that, trust the server count.
  const noticesCount = !canSeeNotices ? 0
    : unreadNotices.length < NOTICE_PAGE_SIZE ? unreadNotices.length
    : Math.max(unreadNotices.length, Number(badges?.notices) || 0);

  const ordersCount = visibleOrders.length + (showPrint ? 1 : 0);
  return {
    visibleOrders, printCount, printKey, showPrint, canSeeNotices,
    unreadNotices, noticesCount, ordersCount, total: ordersCount + noticesCount,
  };
};

